import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  Radar,
  Brain,
  Sparkles,
  Workflow,
  Activity,
  Eye,
  Zap,
  TrendingUp,
  Quote,
  Target,
} from "lucide-react";
import { AuroraBackground } from "@/components/aurora-background";
import { GlassOrb } from "@/components/glass-orb";
import { SignalChip } from "@/components/signal-chip";
import { GradientButton } from "@/components/gradient-button";
import { GlassIcon } from "@/components/glass-icon";
import { FinalCta } from "@/components/final-cta";
import { signUpUrl } from "@/lib/cta";
import { MetricCounters } from "@/components/metric-counters";
import { VsTable } from "@/components/vs-table";

import { useCaseMetrics, useCaseModule } from "@/lib/page-metrics";
import { productModules } from "@/components/product-modules";
import { seo } from "@/lib/seo";
import { article, breadcrumb, faqPage } from "@/lib/structured-data";
import { NotFoundPage } from "@/components/not-found-page";
import { SITE_URL } from "@/lib/seo-constants";
import { useCases, type UseCase } from "@/lib/use-cases-data";
import { FaqSection } from "@/components/faq-section";
import { CaseStudiesSection } from "@/components/case-studies-section";
import { getCaseStudyBySlug } from "@/lib/case-studies.functions";
import type { CaseStudy } from "@/lib/sanity";

type LoaderData = { data: UseCase; caseStudies: CaseStudy[] };

export const Route = createFileRoute("/use-cases/$slug")({
  loader: async ({ params }): Promise<LoaderData> => {
    const data = useCases[params.slug];
    if (!data) throw notFound();
    let caseStudies: CaseStudy[] = [];
    if (data.caseStudySlugs && data.caseStudySlugs.length > 0) {
      const results = await Promise.all(
        data.caseStudySlugs.map((slug) => getCaseStudyBySlug({ data: { slug } })),
      );
      caseStudies = results.filter((c): c is CaseStudy => Boolean(c));
    }
    return { data, caseStudies };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return seo({
        title: "Use case not found",
        description: "The use case you're looking for doesn't exist.",
        path: `/use-cases/${params.slug}`,
        noindex: true,
      });
    }
    const { data } = loaderData;
    const path = `/use-cases/${params.slug}`;
    const subject = data.hero.title.replace(/^Axy.digital for /, "");
    const title = data.seoTitle ?? `AI Marketing for ${subject}`;
    const description = data.seoDescription ?? data.hero.tagline;
    const jsonLd: object[] = [
      article({
        headline: data.hero.title,
        description,
        url: `${SITE_URL}${path}`,
      }),
      breadcrumb([
        { name: "Home", url: "/" },
        { name: "Use cases", url: "/use-cases" },
        { name: data.label, url: path },
      ]),
    ];
    if (data.faq && data.faq.length > 0) {
      jsonLd.push(faqPage(data.faq.map((f) => ({ q: f.question, a: f.answer }))));
    }
    return seo({
      title,
      description,
      path,
      type: "article",
      jsonLd,
    });
  },
  component: UseCasePage,
  notFoundComponent: () => <NotFoundPage />,
});


function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-pink-soft mb-4">
      {children}
    </p>
  );
}

const flowIconCycle = [Radar, Brain, Sparkles, Workflow, Activity, Eye, Zap];

function UseCasePage() {
  const { data, caseStudies } = Route.useLoaderData() as LoaderData;
  const params = Route.useParams();
  const metrics = useCaseMetrics[params.slug] ?? [];
  const moduleSlug = useCaseModule[params.slug];
  const Module = moduleSlug ? productModules[moduleSlug] : undefined;


  // Build a synthetic before/after split from problem.bullets and approach.bullets
  const beforeList = data.problem.bullets ?? data.problem.body;
  const afterList = data.approach.bullets;

  return (
    <article className="relative">
      {/* HERO */}
      <section className="relative px-6 pt-20 pb-20 overflow-hidden">
        <AuroraBackground />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <SectionEyebrow>Use case · {data.label}</SectionEyebrow>
            <h1 className="text-5xl md:text-6xl font-semibold tracking-tight text-balance text-white mb-6 leading-[1.05]">
              {data.hero.title.split(" ").map((w, i, arr) =>
                i === arr.length - 1 ? (
                  <span key={i} className="text-gradient">
                    {w}
                  </span>
                ) : (
                  <span key={i}>{w} </span>
                ),
              )}
            </h1>
            <p className="text-lg text-white/80 mb-8 text-pretty">{data.hero.tagline}</p>
            <div className="flex flex-wrap items-center gap-3 mb-10">
              <GradientButton asChild>
                <a href={signUpUrl("usecase-hero")}>
                  {data.hero.cta} <ArrowRight className="size-4" />
                </a>
              </GradientButton>
            </div>
          </div>

          <div className="relative h-[28rem] hidden lg:block">
            <div className="absolute inset-0 grid place-items-center">
              <GlassOrb size="lg" />
            </div>
            <SignalChip
              label="Buyer intent rising"
              icon={TrendingUp}
              className="absolute top-4 left-0"
              delay={0}
            />
            <SignalChip
              label="Citation gap found"
              icon={Quote}
              className="absolute top-24 right-2"
              delay={1.2}
            />
            <SignalChip
              label="Narrative detected"
              icon={Target}
              className="absolute bottom-24 left-2"
              delay={2}
            />
            <SignalChip
              label="Campaign ready"
              icon={Zap}
              className="absolute bottom-6 right-6"
              delay={0.6}
            />
          </div>
          <div className="lg:hidden grid place-items-center pt-4">
            <GlassOrb size="md" />
          </div>
        </div>
      </section>

      {/* OUTCOME BAND */}
      {metrics.length > 0 && (
        <section className="px-6 -mt-2 mb-16">
          <div className="max-w-7xl mx-auto">
            <MetricCounters items={metrics} />
          </div>
        </section>
      )}

      {/* PROBLEM — Vs Table */}
      <section className="px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-10">
            <SectionEyebrow>The problem</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance">
              {data.problem.heading}
            </h2>
            <div className="mt-5 space-y-3 text-white/70 text-pretty">
              {data.problem.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
          <VsTable before={beforeList.slice(0, 6)} after={afterList.slice(0, 6)} />
          {data.problem.closing && (
            <p className="mt-8 text-white/65 max-w-3xl text-pretty">{data.problem.closing}</p>
          )}
        </div>
      </section>

      {/* APPROACH + live module */}
      <section className="relative px-6 py-24 overflow-hidden">
        <AuroraBackground className="opacity-40" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <SectionEyebrow>The Axy.digital approach</SectionEyebrow>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance mb-6">
              {data.approach.heading}
            </h2>
            <div className="space-y-3 text-white/75 text-pretty mb-6">
              {data.approach.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
            <ul className="grid sm:grid-cols-2 gap-2.5">
              {data.approach.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-white/85">
                  <CheckCircle2 className="size-4 mt-0.5 text-pink-soft shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
            {data.approach.closing && (
              <p className="mt-6 text-white/70 text-pretty">{data.approach.closing}</p>
            )}
          </div>
          <div>{Module ? <Module /> : null}</div>
        </div>
      </section>

      {/* WHAT AXY DELIVERS — capabilities + outcomes consolidated */}
      {data.helps.items.length > 0 && (
        <section className="relative px-6 py-28 overflow-hidden">
          <AuroraBackground className="opacity-40" />
          <div className="relative max-w-7xl mx-auto">
            <div className="max-w-3xl mb-12">
              <SectionEyebrow>What Axy.digital delivers</SectionEyebrow>
              <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance">
                {data.helps.heading}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.helps.items.map((item, i) => {
                const Icon = flowIconCycle[i % flowIconCycle.length];
                return (
                  <article
                    key={item.title}
                    className="group relative glass-card p-6 hover-lift hover:-translate-y-1 hover:border-white/25 overflow-hidden transition-all duration-300"
                  >
                    <div
                      aria-hidden
                      className="absolute -top-12 -right-12 size-32 rounded-full blur-2xl opacity-0 group-hover:opacity-60 transition-opacity duration-500"
                      style={{
                        background:
                          "radial-gradient(circle, rgba(217,70,239,0.55), transparent 70%)",
                      }}
                    />
                    <div className="relative flex items-start gap-3 mb-3">
                      <GlassIcon icon={Icon} size="sm" />
                      <p className="text-[10px] uppercase tracking-wider text-pink-soft pt-1.5">
                        {String(i + 1).padStart(2, "0")}
                      </p>
                    </div>
                    <h3 className="relative text-base font-semibold text-white mb-2 text-pretty">
                      {item.title}
                    </h3>
                    <p className="relative text-sm text-white/70 leading-relaxed">{item.body}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {caseStudies.length > 0 && <CaseStudiesSection items={caseStudies} />}

      {data.faq && data.faq.length > 0 && <FaqSection items={data.faq} />}



      <FinalCta
        heading={data.closing.heading}
        body={data.closing.body}
        primaryCta="Set up your marketing engine"
        primaryHref={signUpUrl("usecase-final")}
        secondaryCta="See case studies"
        secondary={{ kind: "link", href: "/case-studies" }}
      />

      <div className="px-6 pb-24 -mt-10">
        <div className="max-w-7xl mx-auto text-center text-sm">
          <Link to="/use-cases" className="text-pink-soft hover:text-white transition-colors">
            ← Back to all use cases
          </Link>
        </div>
      </div>
    </article>
  );
}
